"use client"
import { useCreditFormatters } from "@/hooks/use-credit-formatters"


import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Skeleton } from "@/components/ui/skeleton"
import { usePermissions } from "@/hooks/use-permissions"
import { BILLING_PERMISSIONS, NOTIFICATIONS_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { useBillingSettingsViewQuery } from "@/hooks/queries/billing-settings"
import { useUpdateBillingSettingsViewMutation } from "@/hooks/mutations/billing-settings"
import { parseCreditsToMicroUsd, creditsInputValue } from "@/lib/billing/utils/credits"
import { NOTIFICATION_CHANNELS_HREF } from "@/lib/notifications/page-link"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




export function SpendingAlertsCard() {
  const { formatCredits } = useCreditFormatters()


  const tUi = useTranslations("Billing.BillingSpendingAlertsCard")

  const { can } = usePermissions()
  const canManage = can(BILLING_PERMISSIONS.MANAGE)
  const canManageChannels = can(NOTIFICATIONS_PERMISSIONS.MANAGE)
  const { data, isLoading } = useBillingSettingsViewQuery()
  const update = useUpdateBillingSettingsViewMutation()

  const [enabled, setEnabled] = useState(true)
  const [capEnabled, setCapEnabled] = useState(true)
  const [amount, setAmount] = useState("")
  const [daily, setDaily] = useState("")
  const [monthly, setMonthly] = useState("")
  // Without this, flipping only a toggle would save the inherited default back as an
  // explicit override and silently end inheritance.
  const [amountEdited, setAmountEdited] = useState(false)

  useEffect(() => {
    if (!data) return
    setEnabled(data.lowBalanceEmailEnabled)
    setCapEnabled(data.spendingCapEmailEnabled)
    // The EFFECTIVE threshold: an inheriting tenant sees the number their alert really
    // fires on, not an empty box.
    setAmount(creditsInputValue(data.effectiveThresholdMicrousd))
    setDaily(
      data.dailySpendingLimitMicrousd !== null ? creditsInputValue(data.dailySpendingLimitMicrousd) : "",
    )
    setMonthly(
      data.monthlySpendingLimitMicrousd !== null
        ? creditsInputValue(data.monthlySpendingLimitMicrousd)
        : "",
    )
    setAmountEdited(false)
  }, [data])

  const parsed = parseCreditsToMicroUsd(amount)
  const invalid = parsed === null || parsed < 0
  const inheriting = data != null && data.lowBalanceThresholdMicrousd === null && !amountEdited
  const thresholdPayload = inheriting ? null : parsed

  // Blank means "no limit", which is NOT the same as a parse failure — both would
  // otherwise collapse to null and silently clear the cap.
  const dailyPayload = daily.trim() === "" ? null : parseCreditsToMicroUsd(daily)
  const monthlyPayload = monthly.trim() === "" ? null : parseCreditsToMicroUsd(monthly)
  const dailyInvalid = daily.trim() !== "" && (dailyPayload === null || dailyPayload < 0)
  const monthlyInvalid = monthly.trim() !== "" && (monthlyPayload === null || monthlyPayload < 0)
  const capsInvalid = dailyInvalid || monthlyInvalid

  const dirty =
    data != null &&
    (dailyPayload !== data.dailySpendingLimitMicrousd ||
      monthlyPayload !== data.monthlySpendingLimitMicrousd ||
      enabled !== data.lowBalanceEmailEnabled ||
      capEnabled !== data.spendingCapEmailEnabled ||
      thresholdPayload !== data.lowBalanceThresholdMicrousd)

  const save = () => {
    if (invalid || capsInvalid) return
    update.mutate({
      dailySpendingLimitMicrousd: dailyPayload,
      monthlySpendingLimitMicrousd: monthlyPayload,
      lowBalanceThresholdMicrousd: thresholdPayload,
      lowBalanceEmailEnabled: enabled,
      spendingCapEmailEnabled: capEnabled,
    })
  }

  // Reset only the threshold; preserve saved caps rather than submitting unsaved edits.
  const useDefault = () => {
    if (!data) return
    setAmount(creditsInputValue(data.platformDefaultMicrousd))
    setAmountEdited(false)
    if (data.lowBalanceThresholdMicrousd === null) return // nothing persisted to reset
    update.mutate({
      dailySpendingLimitMicrousd: data.dailySpendingLimitMicrousd,
      monthlySpendingLimitMicrousd: data.monthlySpendingLimitMicrousd,
      lowBalanceThresholdMicrousd: null,
      lowBalanceEmailEnabled: enabled,
      spendingCapEmailEnabled: capEnabled,
    })
  }

  const busy = isLoading || update.isPending || !canManage
  const planDaily = data?.planDailyLimitMicrousd
  const planMonthly = data?.planMonthlyLimitMicrousd

  if (isLoading || !data) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base"><span key={tUi("spendingAlerts")}>{tUi("spendingAlerts")}</span></CardTitle>
          <CardDescription className="text-xs">
            <span key={tUi("capWhatYouCanSpendAndChooseHow")}>{tUi("capWhatYouCanSpendAndChooseHow")}</span></CardDescription>
        </CardHeader>
        <CardContent className="divide-y py-0">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex flex-wrap items-center justify-between gap-4 py-4">
              <div className="min-w-0 basis-40 flex-1 space-y-1.5">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-full max-w-md" />
              </div>
              <Skeleton className="h-8 w-28" />
            </div>
          ))}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base"><span key={tUi("spendingAlerts")}>{tUi("spendingAlerts")}</span></CardTitle>
        <CardDescription className="text-xs">
          <span key={tUi("capWhatYouCanSpendAndChooseHow")}>{tUi("capWhatYouCanSpendAndChooseHow")}</span></CardDescription>
      </CardHeader>

      <CardContent className="divide-y py-0">
        <SettingRow
          htmlFor="cap-daily"
          title={tUi("spendingLimitsCredits")}
          description={tUi("pastALimitNewAIRequestsAreDeclined", { value0: String(planDaily != null || planMonthly != null
              ? ` Plan allows ${[
                  planDaily != null ? `${formatCredits(planDaily)}/day` : null,
                  planMonthly != null ? `${formatCredits(planMonthly)}/month` : null,
                ]
                  .filter(Boolean)
                  .join(", ")}.`
              : "") })}
        >
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground"><span key={tUi("daily")}>{tUi("daily")}</span></span>
            <CreditInput
              id="cap-daily"
              value={daily}
              onChange={setDaily}
              disabled={busy}
              placeholder={tUi("noLimit")}
              ariaLabel="Daily spending limit"
              invalid={dailyInvalid}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground"><span key={tUi("monthly")}>{tUi("monthly")}</span></span>
            <CreditInput
              value={monthly}
              onChange={setMonthly}
              disabled={busy}
              placeholder={tUi("noLimit")}
              ariaLabel="Monthly spending limit"
              invalid={monthlyInvalid}
            />
          </div>
        </SettingRow>

        <SettingRow
          htmlFor="cap-alert"
          title={tUi("alertMeWhenALimitIsReached")}
          description={tUi("soADeclinedRequestIsNeverASurprise")}
        >
          <Switch
            id="cap-alert"
            checked={capEnabled}
            disabled={busy}
            onCheckedChange={setCapEnabled}
          />
        </SettingRow>

        <SettingRow
          htmlFor="low-alert"
          title={tUi("alertMeWhenTheBalanceRunsLowCredits")}
          description={tUi("at0CreditsAIFeaturesPauseUntilYou")}
        >
          <Switch
            id="low-alert"
            checked={enabled}
            disabled={busy}
            onCheckedChange={setEnabled}
          />
          {enabled && (
            <>
              <CreditInput
                value={amount}
                onChange={(v) => {
                  setAmount(v)
                  setAmountEdited(true)
                }}
                disabled={busy}
                ariaLabel="Low balance threshold"
                invalid={invalid}
              />
              {canManage && data != null && data.lowBalanceThresholdMicrousd !== null && (
                <Button variant="ghost" size="sm" onClick={useDefault} disabled={busy}>
                  <span key={tUi("useDefault")}>{tUi("useDefault")}</span></Button>
              )}
            </>
          )}
        </SettingRow>

        <SettingRow
          title={tUi("whereAlertsAreDelivered")}
          description={
            <><TranslationText>{tUi.rich("versafyEmailsYourAccountOwnerByDefaultRoute", { tag0: () => (canManageChannels ? (
                <a className="underline underline-offset-2" href={NOTIFICATION_CHANNELS_HREF}>
                  <span key={tUi("notificationChannels")}>{tUi("notificationChannels")}</span></a>
              ) : (
                "Notification Channels"
              )) })}</TranslationText></>
          }
        />
      </CardContent>

      {canManage && (
        <CardFooter className="justify-end gap-3 border-t py-3">
          {(invalid || capsInvalid) && (
            <span className="mr-auto text-xs text-destructive">
              <span key={tUi("enterAValidAmountOrLeaveALimit")}>{tUi("enterAValidAmountOrLeaveALimit")}</span></span>
          )}
          <Button size="sm" onClick={save} disabled={!dirty || invalid || capsInvalid || busy}>
            <span key={update.isPending ? tUi("saving") : tUi("saveChanges")}>{update.isPending ? tUi("saving") : tUi("saveChanges")}</span>
          </Button>
        </CardFooter>
      )}
    </Card>
  )
}

function SettingRow({
  htmlFor,
  title,
  description,
  children,
}: {
  htmlFor?: string
  title: string
  description: React.ReactNode
  /** Optional: a row can be pure explanation, with its control living elsewhere. */
  children?: React.ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-3">
      <div className="min-w-0 basis-56 flex-1 space-y-0.5">
        <Label htmlFor={htmlFor} className="text-sm font-medium">
          {title}
        </Label>
        <p className="text-xs leading-snug text-muted-foreground">{description}</p>
      </div>
      <div className="flex flex-wrap items-center gap-3">{children}</div>
    </div>
  )
}

function CreditInput({
  id,
  value,
  onChange,
  disabled,
  placeholder,
  ariaLabel,
  invalid,
}: {
  id?: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  placeholder?: string
  ariaLabel?: string
  invalid?: boolean
}) {
  const tUi = useTranslations("Billing.BillingSpendingAlertsCard")

  return (
    <Input
      id={id}
      className="h-8 w-32 tabular-nums"
      inputMode="decimal"
      placeholder={placeholder}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      aria-label={tUi("value0Credits", { value0: String(ariaLabel ?? "Amount") })}
      aria-invalid={invalid}
    />
  )
}
